// deps
import React, { ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// interfaces
import { IUpiPaymentGroup, UpiPaymentMethod } from "@payment-ui/interfaces/Upi";

// helpers
import {
    isNewUpiPaymentMethod,
    getUpiPaymentMethodsByType,
    transformUpiIntentMethods,
    upiAppsRenderingLogic,
} from "@payment-ui/helpers/Upi";
import { isInAppUpiOnboardingMethod } from "@payment-ui/helpers/InAppUpi";
import { AllUpiIntentAvailableMethodsResponse, isEmpty } from "@swiggy-private/payment-core";
import { useSessionStorage } from "@swiggy-private/checkout-core";
import {
    isPaymentMethodSelected,
    getPaymentGroupDisplayName,
    generatePaymentMethodId,
    isPaymentGroupEnabled,
    getPaymentGroupDisableMessage,
    getPaymentGroupIconUrl,
} from "@payment-ui/helpers/Payment";

// components
import PaymentGroupContainer from "../components/Container";
import PaymentGroupTitle from "../components/Title";
import NewUpi from "@payment-ui/components/payment-method/new-upi";
import Upi from "@payment-ui/components/payment-method/upi";
import InAppUpiOnBoarding from "@payment-ui/components/payment-method/inapp-upi-onboarding";
import ShowMoreCta from "@payment-ui/components/payment-group/components/ShowMoreCta";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";
import classNames from "classnames";

// styles
import styles from "../PaymentGroup.v4.scss";

// constants
import { TEXTS } from "./constants";
import { SESSION_STORAGE_KEYS } from "@swiggy-private/core-ui";

interface IUpi extends IUpiPaymentGroup {
    onClickViewAllBtn: (context: string) => void;
    onImpressionViewAll: VoidFunction;
    onClickAddNew: (value: string) => void;
    onImpressionAddNew: VoidFunction;
    onClickPaymentGroups: (context: string, label: string, value: string) => void;
    onClickInAppOnBoarding: () => void;
    onImpressionPaymentGroups: (methodList: string) => void;
    onImpressionPaymentGroupsExpanded: (methodList: string) => void;
}

function UpiPaymentGroup(props: IUpi): ReactElement | null {
    const {
        paymentData,
        availableUpiIntentMethods,
        upiIntentMethodFetchInProgress,
        hideGroupTitle,
        selectedPaymentId,
        paymentActions,
        paymentAmount,
        isFrictionPage,
        onClickViewAllBtn,
        onImpressionViewAll,
        onClickAddNew,
        onImpressionAddNew,
        onClickPaymentGroups,
        onClickInAppOnBoarding,
        onImpressionPaymentGroups,
        onImpressionPaymentGroupsExpanded,
    } = props;
    const { inAppUpiMethod, upiCollectMethods, upiIntentMethods } = getUpiPaymentMethodsByType(props);
    const isGroupEnabled = useMemo(() => isPaymentGroupEnabled(paymentData), [paymentData]);
    const [foldSize, setFoldSize] = useState(paymentData?.foldSize || 2);
    const [upiAppsDetails] = useSessionStorage<AllUpiIntentAvailableMethodsResponse | null>(
        SESSION_STORAGE_KEYS.THIRD_PARTY_UPI_INTENT_APPS_DETAILS,
        null,
    );
    const isFoldSizeSetRef = useRef(false);

    // replacing the third.party upi method with details received from native
    const transformedUpiIntentMethods = transformUpiIntentMethods(upiIntentMethods);

    const availableUpiIntentMethodsList =
        transformedUpiIntentMethods &&
        transformedUpiIntentMethods.filter(method => (availableUpiIntentMethods || []).includes(method.name));

    // showing disabled apps at the end
    const allUPIList = upiAppsRenderingLogic(availableUpiIntentMethodsList as UpiPaymentMethod[], upiCollectMethods);

    const isAddNewUPIPresent = useMemo(() => {
        return allUPIList?.findIndex(paymentMethod => isNewUpiPaymentMethod(paymentMethod)) !== -1;
    }, [allUPIList]);

    const isAddNewUPIPresentAndIsLastIndex = useMemo(() => {
        if (isEmpty(allUPIList)) {
            return false;
        }

        return allUPIList?.findIndex(paymentMethod => isNewUpiPaymentMethod(paymentMethod)) === allUPIList.length - 1;
    }, [allUPIList]);

    // If third.party is present adding extra check of checking if add new vpa is the last index
    const isAddNewUpiPresentData = isEmpty(upiAppsDetails) ? isAddNewUPIPresent : isAddNewUPIPresentAndIsLastIndex;

    const upiMethodsToShow = useMemo(() => {
        const sliceSize = isAddNewUpiPresentData && foldSize + 1 === allUPIList?.length ? foldSize + 1 : foldSize;
        const methodsList = allUPIList?.slice(0, sliceSize);

        if (methodsList?.length < foldSize) {
            const methodNames = methodsList?.map(item => item?.meta?.appName);
            onImpressionPaymentGroups(methodNames?.join(","));
        }

        // adding in app upi method at the top for new users
        return inAppUpiMethod.length > 0 ? [inAppUpiMethod[0], ...methodsList] : methodsList;
    }, [isAddNewUpiPresentData, foldSize, allUPIList, inAppUpiMethod, onImpressionPaymentGroups]);

    const showMoreCta = useMemo(() => {
        const length = isAddNewUpiPresentData ? allUPIList?.length - 1 : allUPIList?.length;
        return foldSize < length;
    }, [foldSize, allUPIList?.length, isAddNewUpiPresentData]);

    const expandUpiIntentList = useCallback(() => {
        onClickViewAllBtn(TEXTS.SHOW_MORE_CTA);

        const methodNames = allUPIList?.map(item => item?.meta?.appName);
        onImpressionPaymentGroupsExpanded(methodNames?.join(","));

        setFoldSize(allUPIList?.length);
    }, [onClickViewAllBtn, onImpressionPaymentGroupsExpanded, allUPIList]);

    useEffect(() => {
        if (isEmpty(upiAppsDetails)) {
            return;
        }

        if (!upiIntentMethodFetchInProgress && !isFoldSizeSetRef.current) {
            const enabledApps = allUPIList?.filter(app => app.enabled);
            // showing only enabled apps in the first fold
            setFoldSize(Math.min(enabledApps.length || foldSize, foldSize));
            isFoldSizeSetRef.current = true;
        }
    }, [upiIntentMethodFetchInProgress, allUPIList, foldSize, upiAppsDetails]);

    if (isEmpty(allUPIList) && isEmpty(inAppUpiMethod)) {
        return null;
    }

    return (
        <PaymentGroupContainer isFrictionPage={isFrictionPage}>
            {!hideGroupTitle && (
                <PaymentGroupTitle
                    title={getPaymentGroupDisplayName(paymentData)}
                    disableMessage={getPaymentGroupDisableMessage(paymentData)}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.UPI}
                    capitalizeTitle={false}
                    groupIcon={getPaymentGroupIconUrl(paymentData)}
                />
            )}
            <div className={styles.paymentGroupContainer}>
                {!isEmpty(upiMethodsToShow) && (
                    <div
                        className={classNames(styles.paymentGroupContainerRow, {
                            [styles.paymentGroupContainerWithoutBorder]: !upiMethodsToShow.length,
                            [styles.paymentGroupContainerColumn]: true,
                        })}
                    >
                        {upiMethodsToShow.map((paymentMethod, index) => {
                            const id = generatePaymentMethodId(paymentMethod);
                            if (isInAppUpiOnboardingMethod(paymentMethod)) {
                                return (
                                    <InAppUpiOnBoarding
                                        key={index}
                                        id={id}
                                        position={index}
                                        methodData={paymentMethod}
                                        isPaymentGroupEnabled={isGroupEnabled}
                                        paymentActions={paymentActions}
                                        paymentAmount={paymentAmount}
                                        onClickInAppOnBoarding={onClickInAppOnBoarding}
                                    />
                                );
                            }
                            if (isNewUpiPaymentMethod(paymentMethod)) {
                                return (
                                    <NewUpi
                                        key={index}
                                        id={id}
                                        position={index}
                                        isPaymentGroupEnabled={isGroupEnabled}
                                        selected={isPaymentMethodSelected(selectedPaymentId, id)}
                                        methodData={paymentMethod}
                                        paymentActions={paymentActions}
                                        onClickAddNew={onClickAddNew}
                                        onImpressionAddNew={onImpressionAddNew}
                                    />
                                );
                            }

                            return (
                                <Upi
                                    key={index}
                                    id={id}
                                    position={index}
                                    selected={isPaymentMethodSelected(selectedPaymentId, id)}
                                    methodData={paymentMethod}
                                    isPaymentGroupEnabled={isGroupEnabled}
                                    paymentActions={paymentActions}
                                    paymentAmount={paymentAmount}
                                    onClickPaymentGroups={onClickPaymentGroups}
                                    isThirdPartyIntentApp={!!paymentMethod.isThirdPartyIntentApp}
                                />
                            );
                        })}
                    </div>
                )}

                {showMoreCta && (
                    <ShowMoreCta
                        handleClick={expandUpiIntentList}
                        dataTestId={PAYMENT_GROUP_TEST_IDS.UPI.MORE_METHODS_CTA}
                        text={TEXTS.SHOW_MORE_CTA}
                        icon={true}
                        onImpressionViewAll={onImpressionViewAll}
                    />
                )}
            </div>
        </PaymentGroupContainer>
    );
}

export default memo(UpiPaymentGroup);
